import "./Home.css";
import React, { useEffect, useRef, useState } from "react";
import { FaInstagram } from "react-icons/fa6";
import { FaFacebook } from "react-icons/fa";
import { MdOutlineEmail } from "react-icons/md";
import { Link, useNavigate } from "react-router-dom";
import ImageInterpolation from "./ImageInterpolation";

const bgImgsData = [
  "figuresLARGE",
  "SweetPain", //THE FIRST IMAGE TO SHOW
  "FeminineUrge",
  "manWatercolor_light",
  "watercolorWoman3_1",
  "lostinlust1",
  "watercolorAngryWoman1",
  "angel",
  "womanSketch",
  "WomanSketchBlackWHite1",
  "watercolorManCrying",
  "watercolor_eye",
];

const clamp01 = (v) => Math.min(1, Math.max(0, v));

function gridColClass(cols) {
  switch (cols) {
    case 1:
      return "col-12";
    case 2:
      return "col-12 col-md-6";
    case 3:
      return "col-12 col-md-6 col-lg-4";
    case 4:
      return "col-12 col-md-6 col-lg-3";
    default:
      return "col-12 col-md-6 col-lg-4";
  }
}

export default function Home() {
  const navigate = useNavigate();

  const scrollRef = useRef(null);
  const [scrollY, setScrollY] = useState(0);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  // Desktop images
  const [img1, setImg1] = useState(bgImgsData[1]);
  const [img2, setImg2] = useState(bgImgsData[0]);
  const [img3, setImg3] = useState(bgImgsData[2]);
  const desktopIndexRef = useRef(3);

  // Mobile image
  const [mobileImg, setMobileImg] = useState(bgImgsData[0]);
  const mobileIndexRef = useRef(1);

  /* ---------- resize ---------- */
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  /* ---------- scroll (container) ---------- */
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;

    const onScroll = () => setScrollY(el.scrollTop || 0);
    el.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  /* ---------- image rotation ---------- */
  useEffect(() => {
    if (isMobile) {
      const interval = setInterval(() => {
        mobileIndexRef.current += 1;
        setMobileImg(bgImgsData[mobileIndexRef.current % bgImgsData.length]);
      }, 6000);
      return () => clearInterval(interval);
    }

    const interval = setInterval(() => {
      desktopIndexRef.current += 1;
      const next = bgImgsData[desktopIndexRef.current % bgImgsData.length];
      if (desktopIndexRef.current % 3 === 0) setImg1(next);
      if (desktopIndexRef.current % 3 === 1) setImg2(next);
      if (desktopIndexRef.current % 3 === 2) setImg3(next);
    }, 6000);

    return () => clearInterval(interval);
  }, [isMobile]);

  /* ---------- sections ---------- */
  const STEP = 520;
  const FADE = 260;

  const SECTIONS = [
    { key: "home", type: "HOME" },

    {
      key: "studio",
      type: "GRID",
      title: "The studio",
      subtitle: "A glimpse into the creative space",
      imgs: ["studio1", "lostinlust4", "lostinlust5"],
      cols: 3,
      rowHeight: "100%",
    },

    // Exhibits as CAROUSEL (2 images)
    {
      key: "exhibits",
      type: "CAROUSEL",
      title: "Exhibits",
      subtitle: "2023-2024",
      imgs: ["AllImages", "ekthesh_1"],
      height: 700,
      autoplayMs: 4500, // 0 disables autoplay
    },

    { key: "contact", type: "CONTACT_CTA" },
  ];

  const lastIndex = SECTIONS.length - 1;
  const scrollHeight = SECTIONS.length * STEP;

  const currentIndex = Math.min(lastIndex, Math.floor(scrollY / STEP));
  const within = scrollY - currentIndex * STEP;

  // last section has NO transition effect
  const isLast = currentIndex === lastIndex;
  const fadeStart = STEP - FADE;
  const t = isLast ? 0 : clamp01((within - fadeStart) / FADE);

  const sectionStyle = (i) => {
    if (isLast) {
      if (i !== lastIndex) return { opacity: 0, pointerEvents: "none", transform: "translateY(-24px)" };
      return { opacity: 1, pointerEvents: "auto", transform: "translateY(0px)" };
    }

    if (i < currentIndex) return { opacity: 0, transform: "translateY(-24px)", pointerEvents: "none" };
    if (i > currentIndex + 1) return { opacity: 0, transform: "translateY(24px)", pointerEvents: "none" };

    if (i === currentIndex) {
      return {
        opacity: 1 - t,
        transform: `translateY(${-t * 18}px)`,
        pointerEvents: t > 0.6 ? "none" : "auto",
      };
    }

    return {
      opacity: t,
      transform: `translateY(${(1 - t) * 18}px)`,
      pointerEvents: t < 0.4 ? "none" : "auto",
    };
  };

  const forceScrollOnWheel = (e) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop += e.deltaY;

    const maxScroll = scrollHeight - el.clientHeight;
    if (el.scrollTop > maxScroll) el.scrollTop = maxScroll;
    if (el.scrollTop < 0) el.scrollTop = 0;
  };

  /* ---------- sticky header ---------- */
  const Header = () => (
    <div className="km-stickyHeader">
      <div className="headerContainer pt-4 pr-5 pl-5 w-100" style={{ paddingBottom: "0.75rem" }}>
        <div className="logoContainer">
          <h1 className="logo">KAIPI MARIA</h1>
        </div>

        <div className="d-flex menuBar">
          <Link to="/" className="nav-item nav-link active" style={{ textDecoration: "underline" }}>
            Home
          </Link>
          <Link to="/Gallery" className="nav-item nav-link">Gallery</Link>
          <Link to="/AboutMe" className="nav-item nav-link">AboutMe</Link>
          <Link to="/Contact" className="nav-item nav-link">Contact</Link>
          <Link to="/Shop" className="nav-item nav-link">Shop</Link>
        </div>
      </div>
    </div>
  );

  const Social = () => (
    <div className="block-layout" style={{ zIndex: 3 }}>
      <a href="https://www.instagram.com/kaipimaria_art/" className="socialIcons">
        <FaInstagram size={30} />
      </a>
      <a href="https://www.facebook.com/profile.php?id=100012918557813" className="socialIcons">
        <FaFacebook size={30} />
      </a>
      <a href="mailto:kaiphmaria001@gmail.com" className="socialIcons">
        <MdOutlineEmail size={30} />
      </a>
    </div>
  );

  const GridSection = ({ title, subtitle, imgs, cols = 3, rowHeight = 320 }) => {
    const colClass = gridColClass(cols);

    return (
      <div className="container km-sectionPadTop" style={{ maxWidth: 1200 }}>
        <div className="km-sectionHeader">
          <h2 style={{ letterSpacing: "0.06em", marginBottom: 6 }}>{title}</h2>
          {subtitle ? <div className="km-sectionSub">{subtitle}</div> : null}
        </div>

        <div className="row mt-4 g-4">
          {imgs.map((name) => (
            <div key={name} className={colClass}>
              <div className="studioCard" style={{ height: rowHeight }}>
                <img src={`/images/Gallery/${name}.jpg`} alt={name} />
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const CarouselSection = ({ title, subtitle, imgs, height = 380, autoplayMs = 0 }) => {
    const [idx, setIdx] = useState(0);
    const startX = useRef(null);

    const n = imgs?.length || 0;

    useEffect(() => {
      if (!autoplayMs || n <= 1) return;
      const i = setInterval(() => setIdx((v) => (v + 1) % n), autoplayMs);
      return () => clearInterval(i);
    }, [autoplayMs, n]);

    const prev = () => setIdx((v) => (v - 1 + n) % n);
    const next = () => setIdx((v) => (v + 1) % n);

    const onTouchStart = (e) => {
      startX.current = e.touches?.[0]?.clientX ?? null;
    };

    const onTouchEnd = (e) => {
      const sx = startX.current;
      if (sx == null) return;
      const ex = e.changedTouches?.[0]?.clientX ?? sx;
      const dx = ex - sx;
      startX.current = null;

      if (Math.abs(dx) < 40) return;
      if (dx > 0) prev();
      else next();
    };

    return (
      <div className="container km-sectionPadTop" style={{ maxWidth: 1200 }}>
        <div className="km-sectionHeader">
          <h2 style={{ letterSpacing: "0.06em", marginBottom: 6 }}>{title}</h2>
          {subtitle ? <div className="km-sectionSub">{subtitle}</div> : null}
        </div>

        <div
          className="km-carousel"
          style={{ height }}
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          <div
            className="km-carouselTrack"
            style={{
              width: `${n * 100}%`,
              transform: `translateX(-${(idx * 100) / n}%)`,
            }}
          >
            {imgs.map((name) => (
              <div key={name} className="km-carouselSlide" style={{ width: `${100 / n}%` }}>
                <img src={`/images/Gallery/${name}.jpg`} alt={name} />
              </div>
            ))}
          </div>

          {n > 1 && (
            <>
              <button className="km-carouselBtn km-carouselBtnPrev" onClick={prev} aria-label="Previous">
                ‹
              </button>
              <button className="km-carouselBtn km-carouselBtnNext" onClick={next} aria-label="Next">
                ›
              </button>

              <div className="km-carouselDots">
                {imgs.map((_, i) => (
                  <button
                    key={i}
                    className={`km-dot ${i === idx ? "is-active" : ""}`}
                    onClick={() => setIdx(i)}
                    aria-label={`Go to slide ${i + 1}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const ContactCTA = () => (
    <div className="container km-sectionPadTop" style={{ maxWidth: 1100 }}>
      <div className="km-ctaCard">
        <h2 style={{ margin: 0, letterSpacing: "0.06em" }}>Contact</h2>
        <p style={{ marginTop: 12, opacity: 0.85, lineHeight: 1.7 }}>
          For commissions, collaborations, prints, or exhibitions — send a message and I’ll get back to you.
        </p>

        <div className="km-ctaRow">
          <a className="km-ctaLink" href="mailto:kaiphmaria001@gmail.com">
            <MdOutlineEmail size={20} /> <span>Email</span>
          </a>

          <a className="km-ctaLink" href="https://www.instagram.com/kaipimaria_art/">
            <FaInstagram size={20} /> <span>Instagram</span>
          </a>

          <a className="km-ctaLink" href="https://www.facebook.com/profile.php?id=100012918557813">
            <FaFacebook size={20} /> <span>Facebook</span>
          </a>
        </div>

        <div style={{ marginTop: 18 }}>
          <button className="km-ctaBtn" onClick={() => navigate("/Contact")}>
            Go to Contact Page
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div ref={scrollRef} className="km-scrollRoot" onWheel={forceScrollOnWheel}>
      <Header />

      <div className="km-scrollSpacer" style={{ height: `${scrollHeight}px` }} />

      {SECTIONS.map((sec, i) => (
        <div key={sec.key} className="km-layer" style={sectionStyle(i)}>
          {sec.type === "HOME" ? (
            <>
              <div className="km-sectionPadTop">
                <div className="bodyContainer overflow-hidden" style={{ height: "100vh" }}>
                  {isMobile ? (
                    <div className="block-background">
                      <img
                        src={`/images/Gallery/${mobileImg}.jpg`}
                        alt={mobileImg}
                        className="block-background__image"
                      />
                    </div>
                  ) : (
                    <div className="row position-absolute justify-content-center w-100 h-100">
                      <div className="col-4 p-5" style={{ width: "30%", height: "80%" }}>
                        <ImageInterpolation img={img1} />
                      </div>
                      <div className="col-4 p-5" style={{ width: "30%", height: "80%" }}>
                        <ImageInterpolation img={img2} />
                      </div>
                      <div className="col-4 p-5" style={{ width: "30%", height: "80%" }}>
                        <ImageInterpolation img={img3} />
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <Social />
            </>
          ) : sec.type === "GRID" ? (
            <GridSection
              title={sec.title}
              subtitle={sec.subtitle}
              imgs={sec.imgs}
              cols={sec.cols}
              rowHeight={sec.rowHeight}
            />
          ) : sec.type === "CAROUSEL" ? (
            <CarouselSection
              title={sec.title}
              subtitle={sec.subtitle}
              imgs={sec.imgs}
              height={sec.height}
              autoplayMs={sec.autoplayMs}
            />
          ) : (
            <ContactCTA />
          )}
        </div>
      ))}
    </div>
  );
}
